import { useRef, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowUpRight } from 'lucide-react';
import { blogPosts } from '../../data/blogPosts';

gsap.registerPlugin(ScrollTrigger);

// Snapshot Home's scroll position at the exact moment the user clicks a Journal card,
// BEFORE react-router navigates and BEFORE the browser's focus-scroll fires on the <a>.
// The overlay reads window.__journalSnapshotY on mount to restore this exact position on close.
const snapshotJournalScroll = (e) => {
  try {
    const y = (window.__lenis && typeof window.__lenis.scroll === 'number')
      ? window.__lenis.scroll
      : (window.scrollY || window.pageYOffset || 0);
    window.__journalSnapshotY = y;
    // Prevent the browser from auto-scrolling the clicked <a> into view on focus
    if (e && e.currentTarget && e.currentTarget.blur) {
      // Blur asynchronously so React can still process the click
      setTimeout(() => { try { document.activeElement && document.activeElement.blur && document.activeElement.blur(); } catch (_) {} }, 0);
    }
  } catch (err) { /* noop */ }
};

const Blog = () => {
  const root = useRef(null);
  const featured = blogPosts.find((p) => p.featured);
  const rest = blogPosts.filter((p) => !p.featured);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.blog-featured-img', { clipPath: 'inset(100% 0 0 0)' }, {
        clipPath: 'inset(0% 0 0 0)', duration: 1.8, ease: 'power2.out',
        scrollTrigger: { trigger: '.blog-featured-img', start: 'top 75%' },
      });
      gsap.utils.toArray('.blog-card').forEach((card, i) => {
        gsap.fromTo(card, { opacity: 0, y: 60 }, {
          opacity: 1, y: 0, duration: 1.5, delay: i * 0.12, ease: 'power2.out',
          scrollTrigger: { trigger: card, start: 'top 82%' },
        });
      });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section id="journal" ref={root} data-testid="blog-section" className="bg-[#F5F0E8] py-32 md:py-48 px-6 md:px-16">
      <div className="flex items-end justify-between mb-20 md:mb-28">
        <div>
          <p className="text-[11px] uppercase tracking-[0.4em] text-[#B85C38] mb-6">The Journal</p>
          <h2 className="font-serif-display font-light text-4xl sm:text-6xl lg:text-7xl leading-[1.05] text-[#2C2C2A]">
            Words for the
            <br />
            <em className="text-[#1A4D38]">quiet moments.</em>
          </h2>
        </div>
      </div>

      {featured && (
        <Link to={`/blog/${featured.slug}`} onClick={snapshotJournalScroll} data-testid="blog-featured-link" className="group grid md:grid-cols-5 gap-8 md:gap-16 mb-24 md:mb-32 items-end">
          <div className="md:col-span-3 blog-featured-img img-zoom overflow-hidden will-change-[clip-path]" style={{ clipPath: 'inset(100% 0 0 0)' }}>
            <img src={featured.image} alt={featured.title} loading="lazy" className="w-full aspect-[16/10] object-cover" />
          </div>
          <div className="md:col-span-2 pb-2">
            <p className="text-[11px] uppercase tracking-[0.35em] text-[#7A9E86] mb-5">{featured.category} · {featured.readTime}</p>
            <h3 className="font-serif-display font-light text-3xl md:text-5xl text-[#2C2C2A] leading-[1.12] mb-6 group-hover:text-[#1A4D38] transition-colors duration-700">
              {featured.title}
            </h3>
            <p className="text-sm md:text-base font-light leading-[1.9] text-[#2C2C2A]/65 mb-8 max-w-md">{featured.excerpt}</p>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#B85C38] link-underline">
              Read More <ArrowUpRight size={14} />
            </span>
          </div>
        </Link>
      )}

      <div className="grid md:grid-cols-3 gap-10 md:gap-14">
        {rest.map((post) => (
          <Link key={post.slug} to={`/blog/${post.slug}`} onClick={snapshotJournalScroll} data-testid={`blog-card-${post.slug}`} className="blog-card group block">
            <div className="img-zoom overflow-hidden mb-7">
              <img src={post.image} alt={post.title} loading="lazy" className="w-full aspect-[4/5] object-cover" />
            </div>
            <p className="text-[10px] uppercase tracking-[0.35em] text-[#7A9E86] mb-4">{post.category} · {post.readTime}</p>
            <h3 className="font-serif-display font-light text-2xl md:text-3xl text-[#2C2C2A] leading-[1.15] mb-4 group-hover:text-[#1A4D38] transition-colors duration-700">
              {post.title}
            </h3>
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#B85C38] link-underline">
              Read More <ArrowUpRight size={12} />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default Blog;
